import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { IMAGES, SITE } from "@/data/content";
import { PageHero } from "@/components/shared/PageHero";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { Reveal, Stagger, Item } from "@/components/shared/Reveal";
import { Logo } from "@/components/shared/Logo";
import { WhyWexoz } from "@/components/home/WhyWexoz";
import { Certifications } from "@/components/about/Certifications";
import { Heritage } from "@/components/about/Heritage";
import { Process } from "@/components/home/Process";
import { ClosingCTA } from "@/components/home/ClosingCTA";

const EXPERTISE = ["IoT", "Fleet management", "Logistics automation", "CCTV", "Telematics", "AI", "Transportation software", "Systems integration"];

export default function About() {
  useSEO({ title: "About Wexoz | Transportation Technology Systems Integrator, Dubai", description: "Wexoz Security & Surveillance LLC is a Dubai-based transportation technology systems integrator helping organizations modernize how they manage vehicles, people, assets and transportation operations." });
  return (
    <>
      <PageHero eyebrow="About Wexoz · Established 2013" lines={["One technology partner", "for your entire", "transportation operation."]} image={IMAGES.dubai}
        text={`${SITE.name} is a Dubai-based transportation technology systems integrator, live since 2013, focused on helping organizations modernize the way they manage vehicles, people, assets and transportation operations.`} />

      <section className="section">
        <div className="wrap grid gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-7 space-y-6 text-base leading-relaxed text-slate-300 md:text-lg">
            <div className="eyebrow">Who we are</div>
            <p>Wexoz brings together hardware, IoT, software platforms, AI, CCTV, telematics, fleet management and logistics automation into practical, integrated solutions. We work with customers to identify operational requirements and build technology solutions around those needs.</p>
            <p>We are not limited to one software platform or one hardware manufacturer. Every recommendation starts with the operation — the vehicles, the people, the systems already in place — and the technology follows.</p>
            <p>The result is a company that can take responsibility for the technology layer of a transportation operation: from understanding the requirement through solution design, hardware, software, integration, deployment and ongoing support.</p>
          </Reveal>
          <div className="lg:col-span-5">
            <Reveal delay={0.1} className="card p-8">
              <Logo className="h-14 w-auto" />
              <div className="mt-6 flex items-center gap-2 text-sm text-slate-400"><MapPin className="h-4 w-4 text-cyan-400" /> {SITE.location} · Since 2013</div>
              <div className="mt-8 eyebrow">Expertise across</div>
              <Stagger className="mt-4 flex flex-wrap gap-2" stagger={0.04}>
                {EXPERTISE.map((e) => <Item key={e} className="rounded-full border border-line px-3.5 py-1.5 text-xs font-medium text-slate-200">{e}</Item>)}
              </Stagger>
            </Reveal>
          </div>
        </div>
      </section>

      <Heritage />

      <Certifications />

      <section data-testid="powered-by-section" className="section bg-[#F4F6FA] text-navy">
        <div className="wrap grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <SectionHeading light eyebrow="Powered by the right technology" title="We deploy proven platforms — where they fit your requirement." />
          </div>
          <Reveal delay={0.1} className="lg:col-span-6 space-y-5 text-base leading-relaxed text-slate-600 md:text-lg">
            <p>Wexoz can deploy and integrate proven transportation technology platforms where appropriate to the customer's requirement.</p>
            <p className="rounded-2xl border border-slate-200 bg-white p-6 text-slate-700 shadow-sm">Wexoz selects from established fleet management, logistics, school transport, video and AI platforms as part of a broader transportation technology solution. Depending on the customer's requirements, Wexoz can also integrate other platforms, hardware and technologies.</p>
            <Link to="/technology" data-testid="about-technology-link" className="inline-flex items-center gap-2 font-semibold text-navy hover:text-cyan-600">Explore the technology ecosystem <ArrowUpRight className="h-4 w-4" /></Link>
          </Reveal>
        </div>
      </section>

      <WhyWexoz />
      <Process />
      <ClosingCTA />
    </>
  );
}
